import { Modal } from './Modal'
import { isIOS, silenceNudge } from '../lib/install'
import type { Pwa } from '../lib/pwa'

interface Props {
  pwa: Pwa
  /** The child's name, so the reason reads like their week and not a feature. */
  profileName: string
  /**
   * Why this is on screen. Opened off a planned week it can say so. Opened
   * anywhere else it cannot, because that week may well be empty.
   */
  reason: 'planned' | 'plain'
  /**
   * Whether closing this counts as being asked. True where it is the ask.
   * False where it is only an explanation someone went looking for, such as
   * the end of onboarding: reading the steps there should not use up the
   * one prompt they get after planning a week.
   */
  silenceOnClose?: boolean
  onClose: () => void
}

/**
 * The one time Five Lunches asks to be installed.
 *
 * Shown after a first week is fully planned, because that is the moment the
 * next thing someone wants is this in the kitchen or the shop. Asking earlier
 * is an interruption. Asking again is nagging, so closing it any way at all
 * silences it for good on this device.
 *
 * Three platforms, three honest answers. Chrome and Android can open a real
 * install prompt, so there is a button. iOS cannot, so there are the taps.
 * Anything else gets told where to look.
 */
export function InstallPrompt({ pwa, profileName, reason, silenceOnClose = true, onClose }: Props) {
  const ios = isIOS()

  const close = () => {
    if (silenceOnClose) silenceNudge()
    onClose()
  }

  return (
    <Modal
      title="Keep it on your home screen"
      onClose={close}
      footer={
        <>
          <div className="spacer" />
          <button className="btn" onClick={close}>
            {pwa.canInstall ? 'Not now' : 'Got it'}
          </button>
          {pwa.canInstall && (
            <button
              className="btn primary"
              onClick={() => {
                // Installing always ends the asking, wherever it was opened.
                silenceNudge()
                pwa.install()
                onClose()
              }}
            >
              Add to home screen
            </button>
          )}
        </>
      }
    >
      <p style={{ margin: '0 0 10px' }}>
        {reason === 'planned' ? `${profileName}'s week is planned. ` : ''}
        There is no app to download, but Five Lunches can sit on your home screen like one.
      </p>
      <ul className="install-why">
        <li>Opens full screen, straight to the board.</li>
        <li>Stays signed in, so no sign-in every morning.</li>
        <li>The shopping list works without a signal.</li>
      </ul>

      {pwa.canInstall ? (
        <p className="muted small">
          Your browser can do this in one tap. Nothing is downloaded from an app store.
        </p>
      ) : ios ? (
        <div className="install-how">
          <h3>Three taps in Safari</h3>
          <ol className="install-steps">
            <li>
              Tap <b>Share</b>, the square with an arrow coming out of it.
            </li>
            <li>
              Scroll down and tap <b>Add to Home Screen</b>.
            </li>
            <li>
              Tap <b>Add</b>. The sandwich appears with your other apps.
            </li>
          </ol>
        </div>
      ) : (
        <div className="install-how">
          <h3>In your browser&rsquo;s menu</h3>
          <p>
            Look for <b>Install app</b> or <b>Add to Home Screen</b>.
          </p>
        </div>
      )}
    </Modal>
  )
}
